<template>
  <div v-if="viaje" class="card-viaje mb-10">
    <div class="imagen-viaje-container">
      <div class="imagen-viaje" :class="{ 'h-160': isMisReservas }">
        <img :src="getExperienceImage()" class="imagen" alt="Trip Image" />
      </div>

      <div v-if="showButtons" class="buttons-wrapper-overlay">
        <div v-if="qrVisible && showQrButton" class="button-container pointer" @click="showModalQr">
          <div class="icon-wrapper">
            <CustomIcon :icon="qrIcon" size="30px" size-mobile="16px" class="btn-overlay-color" />
          </div>
          <div class="btn-overlay-color button-text">
            {{ $t('botones.verBono') }}
          </div>
        </div>

        <div
          v-if="docVisible && resumenReservas[0]?.hasBonoAdjunto"
          class="button-container pointer"
          @click="descargarPdf">
          <div class="icon-wrapper">
            <CustomIcon :icon="downloadIcon" size="30px" size-mobile="16px" class="btn-overlay-color" />
          </div>
          <div class="btn-overlay-color button-text">
            {{ $t('general.pdf') }}
          </div>
        </div>
      </div>
    </div>

    <div :class="isMisReservas ? 'px-20 py-15' : 'p-15'">
      <div class="d-flex align-items-center mb-10 flex-wrap">
        <div class="text-landing semibold" :class="isMisReservas ? 'h5' : 'h6'">
          {{ viaje.nombre }}
        </div>
        <div
          v-if="viaje.poweredBy && !checkDomain(HIDE_POWERED_BY_DOMAINS)"
          class="text-landing ms-3 d-flex align-items-center powered-by-wrapper">
          <span class="p pe-1">{{ $t('header.poweredBy') }}</span>
          <img
            :alt="viaje.poweredBy.nombre"
            class="img-logo-powered"
            :title="viaje.poweredBy.nombre"
            :src="viaje.poweredBy.descripcion" />
        </div>
      </div>

      <div class="d-flex align-items-center mb-2">
        <CustomIcon :icon="locationIcon" class="me-2 text-landing" />
        <span class="p text-landing">{{ viaje.lugarExperiencia?.nombre }}</span>
      </div>
      <div class="d-flex align-items-center">
        <CustomIcon :icon="calendaryIcon" class="me-2 text-landing" />
        <span class="p text-landing">
          {{ formatDateDDmmm(viaje.fechaInicio) }}
          <template v-if="viaje.fechaInicio !== viaje.fechaFin"> - {{ formatDateDDmmm(viaje.fechaFin) }}</template>
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import CustomIcon from '@/components/CustomIcon.vue';
  import calendaryIcon from '@/assets/icons/calendar.svg';
  import locationIcon from '@/assets/icons/ubicacion.svg';
  import { informacionHabitacion } from '@/store_pinia/informacionHabitacion';
  import { agenciaStoreModule } from '@/store_pinia/agencia';
  import { formatDateDDmmm } from '@/helpers/dateUtiles';
  import { createToaster } from '@meforma/vue-toaster';
  import ReservasApiService from '@/services/api/ReservasApiService';
  import downloadIcon from '@/assets/icons/download.svg';
  import qrIcon from '@/assets/icons/qr.svg';
  import { ESTADOS_RESERVA } from '@/constants/reservaConstants';
  import { calculateTotalPendiente } from '@/services/utils/GrupoUtils';
  import { useLoadingStore } from '@/store_pinia/loadingStore';
  import { checkDomain, HIDE_POWERED_BY_DOMAINS } from 'src/helpers/domainUtils';

  const { t: $t } = useI18n();
  const toaster = createToaster({ position: 'top-right' });
  const loadingStore = useLoadingStore();

  const storeHabitacion = informacionHabitacion();
  const agenciaStore = agenciaStoreModule();

  const props = defineProps({
    qrVisible: {
      type: Boolean,
      required: false,
      default: false,
    },
    docVisible: {
      type: Boolean,
      required: false,
      default: false,
    },
    grupoId: {
      type: Number,
      required: false,
      default: null,
    },
    isMisReservas: {
      type: Boolean,
      default: false,
    },
    isB2b: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits(['showModalQr']);

  const viaje = computed(() => storeHabitacion.getViaje);
  const resumenReservas = computed(() => storeHabitacion.getResumenReservas);

  const totalPending = computed(() => {
    return calculateTotalPendiente(resumenReservas.value);
  });

  const showQrButton = computed(() => {
    if (!resumenReservas.value || resumenReservas.value.length === 0) {
      return false;
    }
    const isNotCancelled = resumenReservas.value.some(({ estado }) => estado !== ESTADOS_RESERVA.CANCELADA);
    return isNotCancelled && totalPending.value <= 0;
  });

  const showButtons = computed(() => {
    if (props.isMisReservas) return false;
    const hasPdf = props.docVisible && resumenReservas.value[0]?.hasBonoAdjunto;
    return (props.qrVisible && showQrButton.value) || hasPdf;
  });

  function getExperienceImage() {
    return viaje.value.imagenExperiencia || agenciaStore.getImagenFondo;
  }

  function showModalQr() {
    emit('showModalQr');
  }

  async function descargarPdf() {
    if (!props.docVisible) return;

    try {
      loadingStore.show();
      const response = await ReservasApiService.getBonoAdjunto(props.grupoId, Number(resumenReservas.value[0].id));
      window.open(response, '_blank');
    } catch (error) {
      console.error('Error al descargar el PDF:', error);
    } finally {
      loadingStore.hide();
    }
  }
</script>

<style lang="scss" scoped>
  .card-viaje {
    border-radius: var(--bs-border-radius-xl);
    border: 1px solid rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.2);
    overflow: hidden;
    background: var(--landing-background-color, white);
    font-family: var(--landing-text-font, inherit);
  }

  .imagen-viaje-container {
    position: relative;

    .imagen-viaje {
      height: 130px;
      overflow: hidden;

      &.h-160 {
        height: 160px;

        @media screen and (max-width: 768px) {
          height: 120px;
        }
      }

      .imagen {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }
  }

  .buttons-wrapper-overlay {
    position: absolute;
    top: 0;
    right: 0;
    display: flex;
    gap: 20px;
    padding-top: 20px;
    padding-right: 20px;
    z-index: 10;

    @media screen and (max-width: 768px) {
      flex-direction: column;
      gap: 8px;
      padding-top: 10px;
      padding-right: 10px;
    }
  }

  .button-container {
    background-color: var(--bs-white);
    border-radius: var(--bs-border-radius-xl);
    padding: 8px;
    height: 70px;
    width: 70px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;

    @media screen and (max-width: 768px) {
      height: 26px;
      width: 100%;
      padding: 4px 8px;
      border-radius: var(--bs-border-radius-lg);
      flex-direction: row;
      justify-content: flex-start;
      gap: 4px;
    }

    .icon-wrapper {
      height: 50px;
      display: flex;
      align-items: center;
      justify-content: center;

      @media screen and (max-width: 768px) {
        height: auto;
        width: auto;
      }
    }

    .btn-overlay-color {
      color: var(--landing-detail-color, var(--bs-primary));
      font-size: 13px;
      font-weight: 600;

      @media screen and (max-width: 768px) {
        font-size: 13px;
      }
    }

    &:hover {
      background-color: rgba(var(--bs-secondary-rgb), 1);
    }

    &.disabled {
      pointer-events: none;
      opacity: 0.6;
    }
  }

  .powered-by-wrapper {
    @media screen and (max-width: 768px) {
      width: 100%;
      margin-left: 0 !important;
      margin-top: 8px;
    }
  }

  .img-logo-powered {
    transition: 0.4s;
    max-width: 60px;
    max-height: 60px;
    object-fit: contain;
    object-position: left;
  }

  .button-text {
    font-size: 13px;
    display: block;
    text-align: center;

    @media screen and (max-width: 768px) {
      font-size: 13px;
      margin-top: 0 !important;
      display: inline;
      text-align: left;
    }
  }
</style>
